import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import type { BillRunMetrics } from '../../lib/billRunMetrics';
import type { BillRunConditions } from '../../lib/billRunConditions';
import type { BillRunCoverage } from '../../lib/billRunRows';
import { ASSIGNED_LABELS, ASSIGNED_ORDER } from '../../lib/billRunStatus';
import { SEND_STATUS_LABELS } from '../../lib/reportStatus';
import { SEND_STATUS_ORDER } from '../../lib/billFilters';
import { WORKFLOW_LABELS, WORKFLOW_ORDER, type BillWorkflowStatus } from '../../lib/billWorkflow';

// The metric cards (US-009). Each card's number follows every filter except
// its own group's, and clicking it toggles that condition.

export type MetricGroup = 'coverage' | 'outcome' | 'progress' | 'assigned' | 'send';

type BillRunsMetricsProps = {
  /** The groups this tab shows, in order. */
  show: readonly MetricGroup[];
  metrics: BillRunMetrics;
  conditions: BillRunConditions;
  onConditions: (patch: Partial<BillRunConditions>) => void;
};

const fmtNum = (n: number) => n.toLocaleString('en-AU');
/** `n` as a % of `of`, for a card's sub line; empty when there is nothing to divide by. */
const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 1000) / 10}%` : '');

/** Progress card colour: where a bill waits on us (red/amber), is with the client (none), or is done (green). */
const PROGRESS_TONE: Partial<Record<BillWorkflowStatus, CardProps['tone']>> = {
  missing_invoice: 'amber', missing_data: 'amber', mismatched: 'red', internal_review: 'orange',
  system_review: 'orange', follow_up_retailer: 'red', ready_to_send: 'amber', completed: 'green',
};

const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });

/** `xs` with `x` added, or removed when already in it. */
function toggled<T>(xs: readonly T[], x: T): T[] {
  return xs.includes(x) ? xs.filter((v) => v !== x) : [...xs, x];
}

type CardProps = {
  testId: string;
  label: string;
  value: string | number;
  sub?: string;
  active: boolean;
  tone?: 'red' | 'orange' | 'green' | 'amber' | 'none';
  onClick: () => void;
};

const TONE = {
  red: 'text-red-600', orange: 'text-orange-500', green: 'text-emerald-600', amber: 'text-amber-600', none: 'text-gray-900',
} as const;

const Card = ({ testId, label, value, sub, active, tone = 'none', onClick }: CardProps) => (
  <button
    type="button"
    data-testid={`bill-runs2-card-${testId}`}
    data-active={active}
    onClick={onClick}
    className={cn(
      'text-left rounded-lg border bg-white px-3 py-2 shadow-sm transition-colors hover:bg-gray-50 min-w-[7.5rem]',
      active ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200',
    )}
  >
    <p className="text-[11px] text-gray-500">{label}</p>
    <p className={cn('text-lg font-semibold leading-tight', TONE[tone])}>{typeof value === 'number' ? fmtNum(value) : value}</p>
    {sub && <p className="text-[11px] text-gray-500">{sub}</p>}
  </button>
);

type SectionProps = {
  id: MetricGroup;
  title: string;
  /** A card in this group is filtering the table. */
  filtering: boolean;
  children: React.ReactNode;
};

/** One metric group: its title, then its cards. */
const Section = ({ id, title, filtering, children }: SectionProps) => (
  <div data-testid={`bill-runs2-section-${id}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
      {title}{filtering && <span className="ml-2 normal-case tracking-normal font-normal text-blue-700">filtering</span>}
    </p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export function BillRunsMetrics({ show, metrics, conditions: c, onConditions }: BillRunsMetricsProps) {
  const [byMonthOpen, setByMonthOpen] = useState(true);
  const cov = metrics.coverage.total;
  const o = metrics.outcome;
  const send = metrics.send;
  const judged = o.matched + o.overcharge + o.undercharge + o.mismatchPending;
  const reports = SEND_STATUS_ORDER.reduce((n, st) => n + (st === 'no_report' ? 0 : send[st] ?? 0), 0);
  const bills = WORKFLOW_ORDER.reduce((n, st) => n + metrics.progress[st], 0);
  const covCard = (key: BillRunCoverage, label: string, n: number, tone?: CardProps['tone'], sub?: string) => (
    <Card
      testId={`coverage-${key}`} label={label} value={n} tone={tone} sub={sub || undefined}
      active={c.coverage.includes(key)}
      onClick={() => onConditions({ coverage: toggled(c.coverage, key) })}
    />
  );

  return (
    <div data-testid="bill-runs2-metrics" className="flex flex-wrap gap-x-8 gap-y-3">
      {show.includes('coverage') && (<Section
        id="coverage" title="Coverage" filtering={c.coverage.length > 0 || c.neverBilled}
      >
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 min-w-[7.5rem]">
          <p className="text-[11px] text-gray-500">Expected</p>
          <p data-testid="bill-runs2-expected" className="text-lg font-semibold leading-tight text-gray-900">{fmtNum(cov.expected)}</p>
        </div>
        {covCard('has_bill', 'Has bill', cov.hasBill, 'green', pct(cov.hasBill, cov.expected))}
        {covCard('missing', 'Missing', cov.missing, cov.missing ? 'red' : 'none')}
        <Card testId="coverage-never-billed" label="Never billed" value={metrics.coverage.neverBilled}
          sub={cov.missing ? `${pct(metrics.coverage.neverBilled, cov.missing)} of missing` : undefined}
          tone={metrics.coverage.neverBilled ? 'red' : 'none'}
          active={c.neverBilled} onClick={() => onConditions({ neverBilled: !c.neverBilled })} />
        {covCard('covered', 'Covered', cov.covered)}
        {covCard('not_expected', 'Not expected', cov.notExpected)}
        {metrics.coverage.byMonth.length > 1 && (
          <div className="basis-full">
            <button type="button" data-testid="bill-runs2-by-month-toggle" onClick={() => setByMonthOpen((x) => !x)}
              className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800">
              {byMonthOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} By month ({metrics.coverage.byMonth.length})
            </button>
            {byMonthOpen && (
              <div data-testid="bill-runs2-by-month" className="mt-1 text-[11px] text-gray-600 space-y-0.5">
                {metrics.coverage.byMonth.map((m) => (
                  <p key={m.month}>{monthLabel(m.month)}: {fmtNum(m.expected)} expected · {fmtNum(m.hasBill)} with a bill · {fmtNum(m.missing)} missing</p>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>)}

      {show.includes('outcome') && (<Section
        id="outcome" title="Outcome" filtering={c.status.length + c.details.length > 0}
      >
        <Card testId="status-matched" label="Matched" value={o.matched} tone="green" sub={judged ? `${pct(o.matched, judged)} pass rate` : undefined}
          active={c.status.includes('matched')} onClick={() => onConditions({ status: toggled(c.status, 'matched') })} />
        <Card testId="details-overcharge" label="Overcharge" value={o.overcharge} tone="red"
          active={c.details.includes('overcharge')} onClick={() => onConditions({ details: toggled(c.details, 'overcharge') })} />
        <Card testId="details-undercharge" label="Undercharge" value={o.undercharge} tone="orange"
          active={c.details.includes('undercharge')} onClick={() => onConditions({ details: toggled(c.details, 'undercharge') })} />
        <Card testId="outcome-no-bill" label="No bill yet" value={o.noBill} tone={o.noBill ? 'amber' : 'none'}
          active={c.coverage.includes('missing')} onClick={() => onConditions({ coverage: toggled(c.coverage, 'missing') })} />
        <Card testId="status-pending" label="Awaiting validation" value={o.awaitingValidation}
          active={c.status.includes('pending')} onClick={() => onConditions({ status: toggled(c.status, 'pending') })} />
      </Section>)}

      {show.includes('progress') && (<Section
        id="progress" title={`Progress · ${fmtNum(bills)} bills`} filtering={c.workflow.length > 0}
      >
        {/* Empty statuses are left out, unless one is filtering, so the row reads as where bills are. */}
        {WORKFLOW_ORDER.filter((st) => metrics.progress[st] > 0 || c.workflow.includes(st)).map((st) => (
          <Card key={st} testId={`progress-${st}`} label={WORKFLOW_LABELS[st]} value={metrics.progress[st]}
            sub={pct(metrics.progress[st], bills) || undefined} tone={metrics.progress[st] ? PROGRESS_TONE[st] : 'none'}
            active={c.workflow.includes(st)} onClick={() => onConditions({ workflow: toggled(c.workflow, st) })} />
        ))}
        {bills === 0 && <p className="text-xs text-gray-400 py-2">No bills.</p>}
      </Section>)}

      {show.includes('assigned') && (<Section
        id="assigned" title="Assigned status" filtering={c.assigned.length > 0}
      >
        {ASSIGNED_ORDER.map((a) => (
          <Card key={a} testId={`assigned-${a}`} label={ASSIGNED_LABELS[a]} value={metrics.assigned[a] ?? 0}
            active={c.assigned.includes(a)} onClick={() => onConditions({ assigned: toggled(c.assigned, a) })} />
        ))}
      </Section>)}

      {show.includes('send') && (<Section
        id="send" title="Send status" filtering={c.send.length > 0}
      >
        {SEND_STATUS_ORDER.map((st) => (
          <Card key={st} testId={`send-${st}`} label={SEND_STATUS_LABELS[st]} value={send[st] ?? 0}
            sub={st === 'no_report' ? undefined : pct(send[st] ?? 0, reports) || undefined}
            tone={st === 'send_failed' && send[st] ? 'red' : 'none'}
            active={c.send.includes(st)} onClick={() => onConditions({ send: toggled(c.send, st) })} />
        ))}
      </Section>)}
    </div>
  );
}
